import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { DatePicker } from "@/components/ui/date_picker";
import { useUpdateLastAuditDate, UpdateKnowledgeItemValidation, useGetKnowledgeItem } from "@/lib/api/knowledge";

interface UpdateLastAuditDateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  taskId: string;
}

export function UpdateLastAuditDateDialog({
  open,
  onOpenChange,
  taskId,
}: UpdateLastAuditDateDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const updateLastAuditDate = useUpdateLastAuditDate();
  
  // Fetch the full knowledge item to get all required fields
  const knowledgeItemQuery = useGetKnowledgeItem(taskId);
  const knowledgeItem = knowledgeItemQuery.data?.data;

  // Reset date when dialog opens or knowledge item loads
  useEffect(() => {
    if (open && knowledgeItem?.last_audit_date) {
      setSelectedDate(new Date(knowledgeItem.last_audit_date));
    } else if (open) {
      setSelectedDate(undefined);
    }
  }, [open, knowledgeItem?.last_audit_date]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!taskId) {
      toast({
        title: "Error",
        description: "Task ID is missing",
        variant: "destructive",
      });
      return;
    }

    if (!knowledgeItem) {
      toast({
        title: "Error",
        description: "Task data not loaded",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);

    try {
      // Build the full update payload using UpdateKnowledgeItemValidation
      // The API will only update last_audit_date but expects all fields
      const tags = knowledgeItem.Tags?.map((tag: { id: string }) => tag.id) || [];
      const steps = (knowledgeItem.OriginalSteps || knowledgeItem.steps || []).map((step: any) => ({
        id: step.id,
        name: step.title || step.name || "",
        title: step.title || step.name || "",
        description: step.description || "",
        blocking: step.is_blocker ?? step.blocking ?? true,
        completion_by: step.completion_by || 'trainer',
      }));

      const parseResult = UpdateKnowledgeItemValidation.safeParse({
        id: taskId,
        title: knowledgeItem.title,
        description: knowledgeItem.description || "",
        last_audit_date: selectedDate ? selectedDate.toISOString() : undefined,
        content: knowledgeItem.content || "",
        special_permission_id: knowledgeItem.special_permission_id || undefined,
        tags,
        main_category_id: knowledgeItem.main_category_id || null,
        secondary_category_id: knowledgeItem.secondary_category_id || null,
        steps,
      });

      if (!parseResult.success) {
        toast({
          title: "Invalid input",
          description: "Please check your inputs",
          variant: "destructive",
        });
        return;
      }

      const result = await updateLastAuditDate(parseResult.data);

      if (!result.success) {
        throw new Error(result.message || "Failed to update");
      }

      toast({
        title: "Updated",
        description: "Last audit date has been updated.",
      });

      onOpenChange(false);
    } catch (error) {
      console.error("Error updating last audit date:", error);
      toast({
        title: "Error",
        description: "Failed to update last audit date. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Don't render until we have the knowledge item data
  if (open && !knowledgeItem && knowledgeItemQuery.isLoading) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Update Last Audit Date</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="lastAuditDate">Last Audit Date</Label>
              <DatePicker
                value={selectedDate}
                onChange={setSelectedDate}
                placeholder="Select a date"
              />
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? "Saving..." : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
